import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { MBTI } from 'src/app/interfaces/mbti'
import {
  DayPillarAnalysisResultResponse,
  DohwaCardResponse,
  DohwaResultResponse,
  DohwaType,
  SajuCreateRequestBody,
  SajuCreateResponse,
  SajuFriendCardPaginatedListResponse,
  SajuListResponse,
  SajuMetaResponse,
  SajuResultResponse,
  SajuSurveyListResponse,
  SajuTestListResponse,
  SearchCityPaginatedResponse,
} from 'src/app/interfaces/saju'
import { environment } from 'src/environments/environment'
import { CustomHttpClient } from '../http/http.service'

@Injectable({
  providedIn: 'root',
})
export class SajuService {
  constructor(private http: CustomHttpClient) {}

  fetchCities(name: string, cursor: string | null, pageSize: number = 10): Observable<SearchCityPaginatedResponse> {
    const queryParams = { name, cursor: cursor ?? null, pageSize: pageSize ?? 10 }
    return this.http.get<SearchCityPaginatedResponse>(`${environment.apiUrl}/v1/geo/cities`, { queryParams })
  }

  createSaju(body: SajuCreateRequestBody): Observable<SajuCreateResponse> {
    return this.http.post<SajuCreateResponse>(`${environment.apiUrl}/v1/saju-profiles`, body)
  }

  /* 안쓰임 */
  updateSaju(sajuId: number, body: SajuCreateRequestBody): Observable<SajuResultResponse> {
    return this.http.patch<SajuResultResponse>(`${environment.apiUrl}/v1/saju-profiles/${sajuId}`, body)
  }

  /** 사주 결과 가져오기 */
  fetchSajuResult(sajuId: number, userIdentifier?: string): Observable<SajuResultResponse> {
    return this.http.get<SajuResultResponse>(`${environment.apiUrl}/v1/saju-profiles/${sajuId}`, {
      queryParams: userIdentifier ? { userIdentifier } : {},
    })
  }

  /** 내 사주 목록 가져오기 */
  fetchSajuList(userIdentifier?: string): Observable<SajuListResponse> {
    return this.http.get<SajuListResponse>(`${environment.apiUrl}/v1/saju-profiles/mine`, {
      queryParams: userIdentifier ? { userIdentifier } : { pageSize: 40 },
    })
  }

  /** 메인 사주 결과 가져오기 */
  fetchMainSajuResult(): Observable<SajuResultResponse> {
    return this.http.get<SajuResultResponse>(`${environment.apiUrl}/v1/saju-profiles/main/mine`)
  }

  /** 메인 사주 고르기 */
  selectMainSaju(sajuId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/saju-profiles/${sajuId}/main`)
  }

  fetchSajuTestResult(sajuId: number): Observable<SajuTestListResponse> {
    return this.http.get<SajuTestListResponse>(`${environment.apiUrl}/v1/saju-profiles/${sajuId}/tests`)
  }

  fetchSajuSurveyList(): Observable<SajuSurveyListResponse> {
    return this.http.get<SajuSurveyListResponse>(`${environment.apiUrl}/v1/saju/surveys`)
  }

  voteSajuSurvey(surveyId: number): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/v1/saju/surveys/${surveyId}/votes`)
  }

  fetchSajuFriendCardList(
    sajuId: number,
    cursor: string | null,
    pageSize: number = 10
  ): Observable<SajuFriendCardPaginatedListResponse> {
    const queryParams = { cursor: cursor ?? null, pageSize: pageSize ?? 10 }
    return this.http.get<SajuFriendCardPaginatedListResponse>(
      `${environment.apiUrl}/v1/saju-profiles/${sajuId}/friend-cards`,
      {
        queryParams,
      }
    )
  }

  /** 도화살 결과 가져오기 */
  fetchDohwaResult(sajuId: number, userIdentifier?: string): Observable<DohwaResultResponse> {
    return this.http.get<DohwaResultResponse>(`${environment.apiUrl}/v1/saju-profiles/${sajuId}/dohwasal`, {
      queryParams: userIdentifier ? { userIdentifier } : {},
    })
  }

  /** 도화살 생성 */
  createDohwa(sajuId: number, userIdentifier?: string): Observable<DohwaResultResponse> {
    return this.http.post<DohwaResultResponse>(`${environment.apiUrl}/v1/saju-profiles/${sajuId}/dohwasal`, {
      queryParams: userIdentifier ? { userIdentifier } : {},
    })
  }

  /** 도화살 카드별 설명 가져오기 */
  fetchDohwaCard(type: DohwaType): Observable<DohwaCardResponse> {
    return this.http.get<DohwaCardResponse>(`${environment.apiUrl}/v1/saju/dohwasal/types/${type}`)
  }

  /** 사주 메타 가져오기 */
  fetchSajuMeta(): Observable<SajuMetaResponse> {
    return this.http.get<SajuMetaResponse>(`${environment.apiUrl}/v1/saju/meta`)
  }

  /** 로그인시, 비회원으로 만든 사주 연결 */
  connectAnonymousSaju(userIdentifier: string): void {
    const body = { userIdentifier }
    this.http.post<void>(`${environment.apiUrl}/v1/saju-profiles/connect`, body).subscribe()
  }

  createDayPillarAnalysis(
    sajuId: number,
    body: { mbti?: MBTI; mbtiQuizId?: number },
    uuid?: string
  ): Observable<DayPillarAnalysisResultResponse> {
    return this.http.post<DayPillarAnalysisResultResponse>(
      `${environment.apiUrl}/v1/saju-profiles/${sajuId}/day-pillar-analysis`,
      body,
      { queryParams: { userIdentifier: uuid ?? null } }
    )
  }

  fetchDayPillarAnalysis(sajuId: number, uuid?: string): Observable<DayPillarAnalysisResultResponse> {
    return this.http.get<DayPillarAnalysisResultResponse>(
      `${environment.apiUrl}/v1/saju-profiles/${sajuId}/day-pillar-analysis`,
      { queryParams: { userIdentifier: uuid ?? null } }
    )
  }
}
